/* eslint-disable max-len */
import { API } from 'aws-amplify';
import {
	ContentType,
	ContentSortInput,
	ContentFilterInput,
	ContentResponse
} from './content_graphql_types';
import {
	ARTICLE_CONTENT_QUERY,
	VIDEO_CONTENT_QUERY,
	PODCAST_CONTENT_QUERY,
	IMAGE_CONTENT_QUERY,
	DOCUMENT_CONTENT_QUERY,
	PLAYLIST_CONTENT_QUERY,
	CATEGORY_CONTENT_QUERY,
	GET_COMPANY_PLAYLISTS_QUERY,
	SEARCH_ARTICLE_QUERY,
	SEARCH_VIDEO_QUERY,
	SEARCH_PODCAST_QUERY,
	SEARCH_IMAGE_QUERY,
	SEARCH_DOCUMENT_QUERY,
	SEARCH_PLAYLIST_QUERY,
	SEARCH_CATEGORY_QUERY,
	SEARCH_SUBCATEGORY_QUERY
} from './content_graphql_queries';
import { toast } from 'react-toastify';
import { GET_UNCATEGORIZED_CATEGORY_ID } from './uncategorized_content_queries';

/**
 * Execute complex content filtering with multiple criteria
 *
 * This function allows filtering content by multiple criteria including content types,
 * category, status, time range, and tags - all in a single query.
 *
 * Based on the example in section 6 "Filtering by Multiple Criteria" from the documentation.
 *
 * @param companyId Company ID
 * @param options Optional parameters for filtering, pagination, etc.
 * @returns Promise resolving to filtered content
 */

/**
 * Get specific content type with filtering options
 *
 * This unified function fetches a specific content type based on the provided
 * content type in the filters. It selects the appropriate query based on the
 * first content type in the filters array.
 *
 * @param companyId Company ID
 * @param options Optional parameters for filtering, pagination, etc.
 * @returns Promise resolving to content data
 */
export const getContent = async (
	companyId: string,
	options: {
        filters?: ContentFilterInput;
        page?: number;
        limit?: number;
        sort?: ContentSortInput;
        from?: 'MyResource' | 'Configuration';
    }
): Promise<ContentResponse> =>
{
	try
	{
		const { filters = {}, page = 1, limit = 5, sort } = options;

		// Determine which content type to fetch
		let contentType: ContentType | undefined;
		let query: string = '';

		if (filters.types && filters.types.length > 0)
		{
			contentType = filters.types[0];
			// Select the appropriate query based on content type (existing logic)
			switch (contentType)
			{
				case ContentType.ARTICLE:
					query = ARTICLE_CONTENT_QUERY;
					break;
				case ContentType.VIDEO:
					query = VIDEO_CONTENT_QUERY;
					break;
				case ContentType.PODCAST:
					query = PODCAST_CONTENT_QUERY;
					break;
				case ContentType.IMAGE:
					query = IMAGE_CONTENT_QUERY;
					break;
				case ContentType.DOCUMENT:
					query = DOCUMENT_CONTENT_QUERY;
					break;
				case ContentType.PLAYLIST:
					query = PLAYLIST_CONTENT_QUERY;
					break;
				case ContentType.CATEGORY:
					query = CATEGORY_CONTENT_QUERY;
					break;
				default:
					toast.error(`Invalid content type ${contentType}`);

					return {
						success : false,
						message : 'Invalid content type',
						data    : []
					};
			}
		}

		// Prepare variables for the GraphQL query
		const variables = {
			companyId,
			filters,
			page,
			limit,
			sort,
			from : options.from || undefined
		};

		// Execute the GraphQL query using AWS Amplify
		const response = await API.post('resourceManagementGraphQL', '/graphql', {
			body : {
				query,
				variables
			}
		});

		// Return the response data
		return response.data.companyContent;
	}
	catch (error)
	{
		console.error('Error fetching content:', error);
		throw error;
	}
};

/**
 * Search content across all types or specific content type
 *
 * This function allows searching for content using a search term.
 * It can search across all content types or be filtered to specific types.
 *
 * @param companyId Company ID
 * @param searchTerm Search term to look for
 * @param options Optional parameters for content type filtering, pagination, filters, and sorting
 * @returns Promise resolving to search results
 */
export const searchContent = async (
	companyId: string,
	searchTerm: string,
	options: {
        contentType?: ContentType;
        page?: number;
        limit?: number;
        filters?: any;
        sort?: any;
        from?: 'MyResource' | 'Configuration';
		parentCategoryId?: string;
    } = {}
): Promise<ContentResponse> =>
{
	try
	{
		const { contentType, page = 1, limit = 10, filters, sort, parentCategoryId } = options;

		// Determine which search query to use based on content type
		let query: string = '';
		const variables: any = {
			companyId,
			searchTerm,
			page,
			limit,
			from : options.from || undefined
		};

		if (parentCategoryId) variables.parentCategoryId = parentCategoryId;

		if (contentType)
		{
			// Add filters and sort to variables if provided
			if (filters) variables.filters = filters;
			if (sort) variables.sort = sort;

			switch (contentType)
			{
				case ContentType.ARTICLE:
					query = SEARCH_ARTICLE_QUERY;
					break;
				case ContentType.VIDEO:
					query = SEARCH_VIDEO_QUERY;
					break;
				case ContentType.PODCAST:
					query = SEARCH_PODCAST_QUERY;
					break;
				case ContentType.IMAGE:
					query = SEARCH_IMAGE_QUERY;
					break;
				case ContentType.DOCUMENT:
					query = SEARCH_DOCUMENT_QUERY;
					break;
				case ContentType.PLAYLIST:
					query = SEARCH_PLAYLIST_QUERY;
					// Add specific handling for playlist filters
					if (filters)
					{
						variables.filters = {
							...filters,
							status : filters.status || 'published'
						};
					}
					else
					{
						variables.filters = { status: 'published' };
					}
					break;
				case ContentType.CATEGORY:
					query = SEARCH_CATEGORY_QUERY;
					break;
				case ContentType.SUBCATEGORY:
					query = SEARCH_SUBCATEGORY_QUERY;
					break;
				default:
					toast.error(`Invalid content type ${contentType}`);

					return {
						success : false,
						message : 'Invalid content type',
						data    : []
					};
			}
		}

		// Execute the GraphQL query using AWS Amplify
		const response = await API.post('resourceManagementGraphQL', '/graphql', {
			body : {
				query,
				variables
			}
		});

		// Return the appropriate response data based on content type
		if (contentType === ContentType.IMAGE)
		{
			return response.data.searchImages;
		}
		else if (contentType === ContentType.VIDEO)
		{
			return response.data.searchVideos;
		}
		else if (contentType === ContentType.DOCUMENT)
		{
			return response.data.searchDocuments;
		}
		else if (contentType === ContentType.ARTICLE)
		{
			return response.data.searchArticles;
		}
		else if (contentType === ContentType.PLAYLIST)
		{
			return response.data.searchPlaylists;
		}
		else if (contentType === ContentType.PODCAST)
		{
			return response.data.searchPodcasts;
		}
		else if (contentType === ContentType.CATEGORY)
		{
			return response.data.searchCategories;
		}
		else if (contentType === ContentType.SUBCATEGORY)
		{
			return response.data.searchSubcategories;
		}
		else if (contentType)
		{
			// For specific content types, use the specific search endpoint
			const searchEndpoints = {
				[ContentType.ARTICLE]  : 'searchArticles',
				[ContentType.VIDEO]    : 'searchVideos',
				[ContentType.PODCAST]  : 'searchPodcasts',
				[ContentType.IMAGE]    : 'searchImages',
				[ContentType.DOCUMENT] : 'searchDocuments'
			};
			const endpoint = searchEndpoints[contentType];

			return response.data[endpoint];
		}
		else
		{
			// Universal search
			return response.data.searchContent;
		}
	}
	catch (error)
	{
		console.error('Error searching content:', error);
		throw error;
	}
};

/**
 * Get network list with filtering options
 *
 * This function retrieves a list of network connections with optional filtering,
 * pagination, and search capabilities.
 *
 * @param options Optional parameters for filtering, pagination, and search
 * @returns Promise resolving to network list data
 */
export const getNetworkList = async (
	options: {
		limit?: number;
		skip?: number;
		search?: string;
		count?: boolean;
		relation?: string[];
	} = {}
): Promise<{
	success: boolean;
	message: string;
	count: number | null;
	data: Array<{
		companyId: string;
		relationName: string;
		isMeSender: boolean;
		companyName: string;
		count: {
			category: number;
			article: number;
			video: number;
			podcast: number;
			document: number;
		};
	}>;
}> =>
{
	try
	{
		const { limit = 10, skip = 0, search = '', count = false, relation = [] } = options;

		// GraphQL query for getting network list
		const query = `
			query GetNetworkList($limit: Int, $skip: Int, $search: String, $count: Boolean, $relation: [String]) {
				getNetworkList(limit: $limit, skip: $skip, search: $search, count: $count, relation: $relation) {
					success
					message
					count
					data {
						companyId
						relationName
						isMeSender
						companyName
						count {
							category
							article
							video
							podcast
							document
						}
					}
				}
			}
		`;

		// Prepare variables for the GraphQL query
		const variables: any = {
			limit,
			skip
		};

		if (count) variables.count = true;
		if (search) variables.search = search;
		if (relation) variables.relation = relation;

		// Execute the GraphQL query using AWS Amplify
		const response = await API.post('resourceManagementGraphQL', '/graphql', {
			body : {
				query,
				variables
			}
		});

		// Return the response data
		return response.data.getNetworkList;
	}
	catch (error)
	{
		console.error('Error fetching network list:', error);
		throw error;
	}
};

/**
 * Get uncategorized category ID for a company
 *
 * This function retrieves the uncategorized category ID for a specific company.
 * This is useful for identifying the default category for uncategorized content.
 *
 * @param companyId Company ID
 * @returns Promise resolving to uncategorized category data
 */
export const getUncategorizedCategoryId = async (
	companyId: string
): Promise<{
	success: boolean;
	message: string;
	data: {
		categoryId: string;
		categoryName: string;
		companyId: string;
	};
}> =>
{
	try
	{
		// Prepare variables for the GraphQL query
		const variables = {
			companyId
		};

		// Execute the GraphQL query using AWS Amplify
		const response = await API.post('resourceManagementGraphQL', '/graphql', {
			body : {
				query : GET_UNCATEGORIZED_CATEGORY_ID,
				variables
			}
		});

		// Return the response data
		return response.data.getUncategorizedCategoryId;
	}
	catch (error)
	{
		console.error('Error fetching uncategorized category ID:', error);
		throw error;
	}
};

// Export all functions
export default {
	getContent,
	searchContent,
	getUncategorizedCategoryId,
	getNetworkList
};
